// Vanilla 1.12 / Classic Era playable classes, in the order WoW's UI uses.
// Colors are the canonical class colors (the same hex codes Blizzard ships
// in the game client and Wowhead).
export interface ClassInfo {
  name: string;
  color: string;
  shortName: string;
  /** Wowhead "classicon_<x>" slug — used for class-only icon fallback. */
  wowheadSlug: string;
}

export const CLASSES: Record<string, ClassInfo> = {
  Druid:   { name: "Druid",   color: "#FF7C0A", shortName: "DRU", wowheadSlug: "druid"   },
  Hunter:  { name: "Hunter",  color: "#AAD372", shortName: "HUN", wowheadSlug: "hunter"  },
  Mage:    { name: "Mage",    color: "#3FC7EB", shortName: "MAG", wowheadSlug: "mage"    },
  Paladin: { name: "Paladin", color: "#F48CBA", shortName: "PAL", wowheadSlug: "paladin" },
  Priest:  { name: "Priest",  color: "#FFFFFF", shortName: "PRI", wowheadSlug: "priest"  },
  Rogue:   { name: "Rogue",   color: "#FFF468", shortName: "ROG", wowheadSlug: "rogue"   },
  Shaman:  { name: "Shaman",  color: "#0070DD", shortName: "SHA", wowheadSlug: "shaman"  },
  Warlock: { name: "Warlock", color: "#8788EE", shortName: "WLK", wowheadSlug: "warlock" },
  Warrior: { name: "Warrior", color: "#C69B6D", shortName: "WAR", wowheadSlug: "warrior" },
};

export const UNKNOWN_CLASS: ClassInfo = {
  name: "Unknown",
  color: "#9CA3AF",
  shortName: "?",
  wowheadSlug: "",
};

export function classFor(type: string | undefined | null): ClassInfo {
  if (!type) return UNKNOWN_CLASS;
  return CLASSES[type] ?? UNKNOWN_CLASS;
}

// Spec slug ("Mage-Frost") -> Wowhead spell-icon slug. WCL's own UI uses
// these icons; Wowhead serves the same images at a stable CDN URL. Covers
// all 27 vanilla 1.12 specs. Specs not in this map fall through to the
// generic class icon, which is always available.
const SPEC_TO_WOWHEAD: Record<string, string> = {
  "Druid-Balance":          "spell_nature_starfall",
  "Druid-Feral":            "ability_druid_catform",
  "Druid-Restoration":      "spell_nature_healingtouch",
  "Hunter-BeastMastery":    "ability_hunter_beasttaming",
  "Hunter-Marksmanship":    "ability_marksmanship",
  "Hunter-Survival":        "ability_hunter_swiftstrike",
  "Mage-Arcane":            "spell_holy_magicalsentry",
  "Mage-Fire":              "spell_fire_firebolt02",
  "Mage-Frost":             "spell_frost_frostbolt02",
  "Paladin-Holy":           "spell_holy_holybolt",
  "Paladin-Protection":     "spell_holy_devotionaura",
  "Paladin-Retribution":    "spell_holy_auraoflight",
  "Priest-Discipline":      "spell_holy_powerwordshield",
  "Priest-Holy":            "spell_holy_guardianspirit",
  "Priest-Shadow":          "spell_shadow_shadowwordpain",
  "Rogue-Assassination":    "ability_rogue_eviscerate",
  "Rogue-Combat":           "ability_backstab",
  "Rogue-Subtlety":         "ability_stealth",
  "Shaman-Elemental":       "spell_nature_lightning",
  "Shaman-Enhancement":     "spell_nature_lightningshield",
  "Shaman-Restoration":     "spell_nature_magicimmunity",
  "Warlock-Affliction":     "spell_shadow_deathcoil",
  "Warlock-Demonology":     "spell_shadow_metamorphosis",
  "Warlock-Destruction":    "spell_shadow_rainoffire",
  "Warrior-Arms":           "ability_warrior_savageblow",
  "Warrior-Fury":           "ability_warrior_innerrage",
  "Warrior-Protection":     "ability_warrior_defensivestance",
};

// Icons are bundled locally under /icons/ — same origin as the app, so
// the browser hits the long-running HTTP/2 connection it already has open
// and caches them aggressively on first paint. The /icons folder is
// committed to the repo and copied into the static build by Vite from
// `frontend/public/`.
const LOCAL_BASE = "/icons";
// Public CDNs are used as last-resort fallbacks if a local file is ever
// missing (e.g. someone added a new spec mapping but forgot to download
// the asset). They will never be hit on the happy path.
const WOWHEAD_BASE = "https://wow.zamimg.com/images/wow/icons/large";

/**
 * URLs to try, in order, when rendering a class/spec icon. The PlayerCell
 * walks this list on `<img onError>`. Order is local-first so initial paint
 * is instant once the browser has cached our static asset.
 */
export function iconUrls(
  icon: string | undefined | null,
  className?: string,
): string[] {
  const urls: string[] = [];

  // 1. Local spec icon (if we have a Wowhead mapping for this spec).
  if (icon && icon.length > 0) {
    const wh = SPEC_TO_WOWHEAD[icon];
    if (wh) urls.push(`${LOCAL_BASE}/${wh}.jpg`);
  }
  // 2. Local class icon — always available for the 9 vanilla classes.
  if (className) {
    const cls = CLASSES[className];
    if (cls?.wowheadSlug) {
      urls.push(`${LOCAL_BASE}/classicon_${cls.wowheadSlug}.jpg`);
    }
  }
  // 3. Remote Wowhead fallbacks (only hit if the local file is missing).
  if (icon && icon.length > 0) {
    const wh = SPEC_TO_WOWHEAD[icon];
    if (wh) urls.push(`${WOWHEAD_BASE}/${wh}.jpg`);
  }
  if (className) {
    const cls = CLASSES[className];
    if (cls?.wowheadSlug) {
      urls.push(`${WOWHEAD_BASE}/classicon_${cls.wowheadSlug}.jpg`);
    }
  }
  return urls;
}
